<template>
    <div class="c-color-picker-wrapper">
        <c-button-group
            v-if="showInputMenu"
            v-model="pickerType"
            class="c-color-picker-menu mb-3 w-100"
            no-icon
            type="radio"
            size="sm"
            wide
            separate
            center
            :items="pickerTypes"
            @input="onChangePickerType"
        />
        <c-solid-color
            v-if="pickerType === ColorPickerType.SOLID"
            v-model="currentColorSolid"
            :allow-reset="allowReset"
            :initial-color="initialColor"
            :reset-text="resetText"
            :mode="mode"
            @input="onUpdateColor"
            @using-eye-dropper="() => emit('using-eye-dropper')"
            @eye-dropper-used="() => emit('eye-dropper-used')"
        />
        <c-gradient-color
            v-if="pickerType === ColorPickerType.GRADIENT"
            v-model="currentColorGradient"
            :mode="mode"
            @input="onUpdateColor"
        />
        <c-palette-color
            v-if="pickerType === ColorPickerType.PALETTE"
            v-model="currentColorPalette"
            :mode="mode"
            @input="onUpdateColor"
        />
        <slot name="footer" />
    </div>
</template>

<script setup lang="ts">
import { ref, Ref, onBeforeMount, onUnmounted } from 'vue';
import CGradientColor from './CGradientColor.vue';
import CSolidColor from './CSolidColor.vue';
import CPaletteColor from './CPaletteColor.vue';
import { type FullGradientObject, type FullPaletteObject, type FullColorObject, ColorPickerType, ColorPickerTypeValue, CColorPickerValue } from '@/composables/color';
import CButtonGroup, { CButtonGroupItem } from '@/components/shared/CButtonGroup';

type ColorPickerMode = 'cmyk' | 'rgb';
type GradientMode = 'linear' | 'radial';

const PICKER_TYPES: CButtonGroupItem<ColorPickerTypeValue>[] = [
    {
        label: 'Solid Color',
        value: ColorPickerType.SOLID
    },
    {
        label: 'Gradient',
        value: ColorPickerType.GRADIENT
    },
    {
        label: 'Alternating Colors',
        value: ColorPickerType.PALETTE
    }
];

interface ColorPickerProps {
    value?: CColorPickerValue;
    angle?: number;
    mode?: ColorPickerMode;
    initialColor?: string;
    allowReset?: boolean;
    resetText?: string;
    hidePalette?: boolean;
    pickerType?: ColorPickerTypeValue;
    gradientMode?: GradientMode;
    showInputMenu?: boolean;
}

const props = withDefaults(defineProps<ColorPickerProps>(), {
    mode: 'rgb',
    angle: 90,
    pickerType: ColorPickerType.SOLID,
    hidePalette: false,
    initialColor: '#8C2525',
    resetText: 'Reset',
    allowReset: true,
    showInputMenu: false
});

const emit = defineEmits<{
    (event: 'input', value: CColorPickerValue): void;
    (event: 'reset', value?: CColorPickerValue): void;
    (event: 'eye-dropper-used'): void;
    (event: 'using-eye-dropper'): void;
}>();

const currentColorGradient: Ref<FullGradientObject | undefined> = ref();
const currentColorSolid: Ref<FullColorObject | undefined> = ref();
const currentColorPalette: Ref<FullPaletteObject | undefined> = ref();
const pickerType: Ref<ColorPickerTypeValue> = ref(props.pickerType || ColorPickerType.SOLID);
const pickerTypes: Ref<CButtonGroupItem<ColorPickerTypeValue>[]> = ref(PICKER_TYPES.filter(item => {
    if (item.value === ColorPickerType.PALETTE && props.hidePalette) return false;
    return true;
}));

const onUpdateColor = (color: CColorPickerValue) => {
    const colorWithType = { ...color, typeColor: pickerType.value };

    switch (pickerType.value) {
        case ColorPickerType.SOLID:
            currentColorSolid.value = colorWithType as FullColorObject;
            break;
        case ColorPickerType.PALETTE:
            currentColorPalette.value = colorWithType as FullPaletteObject;
            break;
        case ColorPickerType.GRADIENT:
            currentColorGradient.value = colorWithType as FullGradientObject;
            break;
    }

    emit('input', color);
};

const resetAllColors = () => {
    currentColorGradient.value = undefined;
    currentColorSolid.value = undefined;
    currentColorPalette.value = undefined;
};

const onChangePickerType = (type: ColorPickerTypeValue) => {
    resetAllColors();
    pickerType.value = type;
};

onBeforeMount(() => {
    if (!props.value) return;

    resetAllColors();

    // Better type detection based on unique properties
    // Its necessary for old versions compatibility
    if ('colorStops' in props.value) {
        currentColorGradient.value = props.value as FullGradientObject;
        pickerType.value = ColorPickerType.GRADIENT;
    } else if ('colors' in props.value) {
        currentColorPalette.value = props.value as FullPaletteObject;
        pickerType.value = ColorPickerType.PALETTE;
    } else {
        currentColorSolid.value = props.value as FullColorObject;
        pickerType.value = ColorPickerType.SOLID;
    }
});

onUnmounted(() => {
    resetAllColors();
    pickerType.value = ColorPickerType.SOLID;
});
</script>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.c-color-picker-wrapper {
    display: flex;
    padding: 10px;
    flex-direction: column;
    box-sizing: initial;
    background: get-color('white');
    border-radius: 0.25rem;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15), 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    :deep() .c-color-picker-menu {
        .btn-group {
            padding: 4px;
            border: 1px solid #e6e6e6;
        }
        .btn-item {
            border: 0;
            background-color: get-color('white');
            color: get-color('black');
            border-top-right-radius: 4px !important;
            border-bottom-right-radius: 4px !important;
            border-top-left-radius: 4px !important;
            border-bottom-left-radius: 4px !important;
            &.active {
                background-color: get-color('purple');
                color: get-color('white');
                font-weight: normal;
            }
        }
    }
}
</style>
